import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueueStore } from '../../stores/queueStore';
import { useTicketStore } from '../../stores/ticketStore';
import { UserContext } from '../../context/UserContext';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@redpanda/ui';
import { useSSE } from '../../hooks/useSSE';
import QueueLimitBanner from '../../components/QueueLimitBanner';

const QueueManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { config, fetchConfig, toggleStatus } = useQueueStore();
  const { waitingList, fetchWaitingList, callNext, issueTicket, updateStatus } = useTicketStore();
  const { user } = useContext(UserContext)!;
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTicketData, setNewTicketData] = useState({ customerName: '', partySize: 1, note: '' });
  const [canAddTicket, setCanAddTicket] = useState(true);
  const [queueLimitRefreshTrigger, setQueueLimitRefreshTrigger] = useState(0);

  const refreshAll = useCallback(() => {
    fetchConfig();
    fetchWaitingList();
  }, []);

  // 初次載入
  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  // SSE 即時更新
  useSSE({
    storeId: user?.organizationId || '',
    enabled: !!user?.organizationId,
    onEvent: () => { refreshAll(); },
  });

  // 降級備援：30 秒輪詢
  useEffect(() => {
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const handleCallNext = async () => {
    try {
      await callNext();
      fetchConfig();
    } catch (e) {}
  };

  const handleIssueTicket = async () => {
    try {
      await issueTicket(newTicketData);
      setNewTicketData({ customerName: '', partySize: 1, note: '' });
      setShowAddForm(false);
      fetchConfig();
      setQueueLimitRefreshTrigger(prev => prev + 1);
    } catch (e) {}
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      WAITING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      NOTIFIED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      CALLED: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      SERVING: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      COMPLETED: 'bg-slate-100 text-gray-800 dark:bg-slate-700 dark:text-gray-200',
      CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      NO_SHOW: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };

    const labels: Record<string, string> = {
      WAITING: t('queue.status.waiting'),
      NOTIFIED: t('queue.status.notified'),
      CALLED: t('queue.status.called'),
      SERVING: t('queue.status.serving'),
      COMPLETED: t('queue.status.completed'),
      CANCELLED: t('queue.status.cancelled'),
      NO_SHOW: t('queue.status.noShow'),
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || ''}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getSourceBadge = (source: string) => {
    const styles: Record<string, string> = {
      ONSITE: 'bg-slate-100 text-gray-700 dark:bg-slate-700 dark:text-gray-300',
      ONLINE: 'bg-brand-100 text-brand-700 dark:bg-brand-900 dark:text-brand-300',
      LINE: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[source] || ''}`}>
        {source}
      </span>
    );
  };

  const getTimeSince = (dateStr: string) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return t('queue.common.justNow');
    return `${diff} ${t('queue.common.minutes')}`;
  };

  const waitingTickets = waitingList.filter(t => ['WAITING', 'NOTIFIED'].includes(t.status));
  const activeTickets = waitingList.filter(t => ['CALLED', 'SERVING'].includes(t.status));

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      {/* 標題 + 操作區 */}
      <div className="flex flex-col md:flex-row md:items-baseline md:justify-between mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 md:mb-0">
          {t('navigation.queueManagement')}
        </h1>
        <div className="flex gap-3">
          <button
            onClick={() => toggleStatus(!config?.isActive)}
            className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
              config?.isActive
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
          >
            {config?.isActive ? t('queue.actions.pauseQueue') : t('queue.actions.startQueue')}
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            disabled={!config?.isActive || !canAddTicket}
            className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
              config?.isActive && canAddTicket
                ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                : 'bg-slate-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('queue.actions.addTicket')}
          </button>
        </div>
      </div>

      {/* 取號數量限制資訊 */}
      <QueueLimitBanner onLimitChange={setCanAddTicket} refreshTrigger={queueLimitRefreshTrigger} />

      {/* 目前服務號碼 + 叫下一號 */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-8 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{t('queue.dashboard.currentServing')}</p>
        <p className="text-7xl font-bold text-brand-600 mb-4">#{config?.currentServing || 0}</p>
        <button
          onClick={handleCallNext}
          disabled={!config?.isActive || waitingTickets.length === 0}
          className={`text-xl font-bold py-4 px-12 rounded-lg transition-all duration-200 ${
            config?.isActive && waitingTickets.length > 0
              ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
              : 'bg-slate-300 dark:bg-slate-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
        >
          {t('queue.actions.callNext')}
        </button>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {t('queue.dashboard.waitingCount')}: {waitingTickets.length} {t('queue.common.groups')}
        </p>
      </div>

      {/* 手動取號表單 */}
      {showAddForm && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.actions.addTicket')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.fields.customerName')}</label>
              <input
                type="text"
                value={newTicketData.customerName}
                onChange={(e) => setNewTicketData({ ...newTicketData, customerName: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                placeholder={t('queue.fields.customerNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.fields.partySize')}</label>
              <input
                type="number"
                min="1"
                max="20"
                value={newTicketData.partySize}
                onChange={(e) => setNewTicketData({ ...newTicketData, partySize: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('queue.fields.note')}</label>
              <input
                type="text"
                value={newTicketData.note}
                onChange={(e) => setNewTicketData({ ...newTicketData, note: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                placeholder={t('queue.fields.notePlaceholder')}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              onClick={handleIssueTicket}
              disabled={!canAddTicket}
              className={`font-bold py-2 px-4 rounded transition-all duration-200 ${
                canAddTicket
                  ? 'bg-brand-600 hover:bg-brand-700 text-white cursor-pointer'
                  : 'bg-slate-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {t('queue.actions.issueTicket')}
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-300 font-bold py-2 px-4 rounded transition-all duration-200"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* 正在服務 */}
      {activeTickets.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('queue.sections.activeService')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTickets.map((ticket) => (
              <div key={ticket.id} className="bg-white dark:bg-slate-800 rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-gray-900 dark:text-white">#{ticket.ticketNumber}</span>
                  {getStatusBadge(ticket.status)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {ticket.customerName || t('queue.common.anonymous')} | {ticket.partySize}{t('queue.common.people')}
                </p>
                <div className="mt-3 flex gap-2">
                  {ticket.status === 'CALLED' && (
                    <button
                      onClick={() => updateStatus(ticket.id, 'SERVING')}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded"
                    >
                      {t('queue.actions.startService')}
                    </button>
                  )}
                  {ticket.status === 'SERVING' && (
                    <button
                      onClick={() => updateStatus(ticket.id, 'COMPLETED')}
                      className="text-xs bg-blue-500 hover:bg-blue-600 text-white py-1 px-3 rounded"
                    >
                      {t('queue.actions.complete')}
                    </button>
                  )}
                  <button
                    onClick={() => updateStatus(ticket.id, 'NO_SHOW')}
                    className="text-xs bg-red-500 hover:bg-red-600 text-white py-1 px-3 rounded"
                  >
                    {t('queue.actions.noShow')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 等候清單 */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('queue.sections.waitingList')} ({waitingTickets.length})
        </h2>
        <div className="bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '700px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t('queue.fields.number')}</TableHead>
                  <TableHead className="w-1/6">{t('queue.fields.customerName')}</TableHead>
                  <TableHead className="w-16">{t('queue.fields.partySize')}</TableHead>
                  <TableHead className="w-20">{t('queue.fields.source')}</TableHead>
                  <TableHead className="w-20">{t('queue.fields.status')}</TableHead>
                  <TableHead className="w-24">{t('queue.fields.waitTime')}</TableHead>
                  <TableHead className="w-1/5">{t('queue.fields.note')}</TableHead>
                  <TableHead className="w-32">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {waitingTickets.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      {t('queue.messages.noWaiting')}
                    </TableCell>
                  </TableRow>
                ) : (
                  waitingTickets.map((ticket) => (
                    <TableRow key={ticket.id}>
                      <TableCell className="font-bold text-lg">#{ticket.ticketNumber}</TableCell>
                      <TableCell>{ticket.customerName || t('queue.common.anonymous')}</TableCell>
                      <TableCell>{ticket.partySize}{t('queue.common.people')}</TableCell>
                      <TableCell>{getSourceBadge(ticket.source)}</TableCell>
                      <TableCell>{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell>{getTimeSince(ticket.issuedAt)}</TableCell>
                      <TableCell className="text-sm text-gray-500 dark:text-gray-400">{ticket.note || '-'}</TableCell>
                      <TableCell>
                        <button
                          onClick={() => updateStatus(ticket.id, 'CANCELLED')}
                          className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('common.cancel')}
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QueueManagementPage;
